import AscIconDark from '@Resources/assets/icons/dark/sort-asc-18x18.svg';
import DescIconDark from '@Resources/assets/icons/dark/sort-desc-18x18.svg';
import {
    ColumnDef,
    Row,
    SortingState,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import _isNil from 'lodash/isNil';
import React, { useState } from 'react';
import EditEntityTableRow from './EditEntityTableRow';
import { isSelectedRow } from './Helpers';
import Styles from './SettingsTable.scss';

export enum SettingsTableViewState {
    CREATE,
    EDIT,
    VIEW,
}

export interface IBaseSettingsTableProps<T extends object> {
    id?: string;
    columns: ColumnDef<T>[];
    data: T[];
    hideTableHeader?: boolean;
}

interface ISettingsTableProps<T extends object>
    extends IBaseSettingsTableProps<T> {
    onRowClick?: (obj: any) => void;
    selectedRow?: any;
    state: SettingsTableViewState;
    updateEntity?: any;
    onChangeUpdateEntity?: (obj: any) => void;
    editCells?: Array<any>;
    createCells?: Array<any>;
    isSubmitting?: boolean;
}

const SettingsTable = <T extends object>(props: ISettingsTableProps<T>) => {
    const [sorting, setSorting] = useState<SortingState>([]);

    const table = useReactTable({
        data: props.data,
        columns: props.columns,
        state: {
            sorting,
        },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    const handleRowClick = (row: Row<T>) => {
        if (props.onRowClick && props.state === SettingsTableViewState.VIEW) {
            props.onRowClick(
                !isSelectedRow(row, props.selectedRow) ? row.original : null
            );
        }
    };

    const getTableRowHtml = (row: Row<T>) => {
        if (
            isSelectedRow(row, props.selectedRow) &&
            props.state === SettingsTableViewState.EDIT
        ) {
            return (
                <EditEntityTableRow
                    id={`${props.id}-table-edit-row`}
                    updateEntity={props.updateEntity}
                    cells={props.editCells}
                    onChangeUpdateEntity={props.onChangeUpdateEntity}
                    key={row.id}
                />
            );
        } else {
            return (
                <tr
                    id={`${props.id}-${row.id}`}
                    key={row.id}
                    onClick={() => handleRowClick(row)}
                    className={
                        isSelectedRow(row, props.selectedRow)
                            ? Styles.selectedRow
                            : null
                    }
                >
                    {row.getVisibleCells().map((cell) => (
                        <td id={cell.id} key={cell.id}>
                            {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                            )}
                        </td>
                    ))}
                </tr>
            );
        }
    };

    return (
        <table id={props.id} className={Styles.table}>
            {!props.hideTableHeader && (
                <thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <tr key={headerGroup.id}>
                            {headerGroup.headers.map((header) => (
                                <th
                                    id={header.id}
                                    key={header.id}
                                    colSpan={1}
                                    onClick={header.column.getToggleSortingHandler()}
                                    className={
                                        header.column.getIsSorted()
                                            ? Styles.sortedTh
                                            : null
                                    }
                                >
                                    <div>
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(
                                                  header.column.columnDef
                                                      .header,
                                                  header.getContext()
                                              )}
                                        {{
                                            asc: <AscIconDark />,
                                            desc: <DescIconDark />,
                                        }[
                                            header.column.getIsSorted() as string
                                        ] ?? null}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
            )}

            <tbody>
                {props.state === SettingsTableViewState.CREATE && (
                    <EditEntityTableRow
                        id={`${props.id}-table-create-row`}
                        updateEntity={props.updateEntity}
                        onChangeUpdateEntity={props.onChangeUpdateEntity}
                        cells={props.createCells}
                        isSubmitting={props.isSubmitting}
                    />
                )}
                {table.getRowModel().rows.map((row) => getTableRowHtml(row))}
            </tbody>
        </table>
    );
};

export default SettingsTable;
